/**
 * 批量修改批次号模态框
 *
 * 支持：
 * 1. 输入新的批次号
 * 2. 检测是否与库中其他数据重复
 * 3. 重复时提示用户选择放弃或强制更新
 */

import React, { useState, useCallback } from "react";
import { Modal, Form, Input, message, Alert, Space, Typography, Spin } from "antd";
import { ExclamationCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { settlementService } from "../../../../api/enhancedApi";

const { Text } = Typography;

const BatchUpdateBatchNumberModal = ({ open, onClose, selectedIds = [], onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [duplicateInfo, setDuplicateInfo] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingBatchNumber, setPendingBatchNumber] = useState("");

    // 重置状态
    const handleClose = useCallback(() => {
        form.resetFields();
        setDuplicateInfo(null);
        setShowConfirmModal(false);
        setPendingBatchNumber("");
        onClose();
    }, [form, onClose]);

    // 提交更新（第一次点击）
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const batchNumber = values.batch_number?.trim();

            if (!batchNumber) {
                message.warning("请输入批次号");
                return;
            }

            setLoading(true);

            // 调用 API 进行更新（不强制）
            const response = await settlementService.batchUpdateBatchNumber({
                ids: selectedIds,
                batch_number: batchNumber,
                force_update: false,
            });

            const result = response.data;

            if (result.success) {
                // 无重复，直接成功
                message.success(`成功更新 ${result.data?.updated_count || selectedIds.length} 条记录的批次号`);
                handleClose();
                onSuccess?.();
            } else if (result.data?.has_duplicates) {
                // 有重复，显示确认对话框
                setDuplicateInfo(result.data);
                setPendingBatchNumber(batchNumber);
                setShowConfirmModal(true);
            } else {
                message.error(result.message || "更新失败");
            }
        } catch (error) {
            console.error("批量更新批次号失败:", error);
            message.error(error.response?.data?.message || error.message || "更新失败");
        } finally {
            setLoading(false);
        }
    };

    // 强制更新（确认重复后）
    const handleForceUpdate = async () => {
        try {
            setLoading(true);

            const response = await settlementService.batchUpdateBatchNumber({
                ids: selectedIds,
                batch_number: pendingBatchNumber,
                force_update: true,
            });

            const result = response.data;

            if (result.success) {
                message.success(`成功更新 ${result.data?.updated_count || selectedIds.length} 条记录的批次号`);
                handleClose();
                onSuccess?.();
            } else {
                message.error(result.message || "强制更新失败");
            }
        } catch (error) {
            console.error("强制更新批次号失败:", error);
            message.error(error.response?.data?.message || error.message || "强制更新失败");
        } finally {
            setLoading(false);
            setShowConfirmModal(false);
        }
    };

    // 放弃更新
    const handleCancelForce = () => {
        setShowConfirmModal(false);
        setDuplicateInfo(null);
        setPendingBatchNumber("");
    };

    return (
        <>
            {/* 主模态框 */}
            <Modal title="批量修改批次号" open={open} onOk={handleSubmit} onCancel={handleClose} okText="确认修改" cancelText="取消" confirmLoading={loading} destroyOnClose width={480}>
                <Spin spinning={loading}>
                    <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                        <Form.Item
                            name="batch_number"
                            label="新批次号"
                            rules={[
                                { required: true, message: "请输入批次号" },
                                { max: 50, message: "批次号不能超过50个字符" },
                            ]}
                            extra={`将为选中的 ${selectedIds.length} 条记录设置统一的批次号`}
                        >
                            <Input placeholder="请输入批次号，如：2026-01-BATCH-001" maxLength={50} showCount autoFocus />
                        </Form.Item>
                    </Form>
                </Spin>
            </Modal>

            {/* 重复确认对话框 */}
            <Modal
                title={
                    <Space>
                        <WarningOutlined style={{ color: "#faad14" }} />
                        <span>批次号重复提醒</span>
                    </Space>
                }
                open={showConfirmModal}
                onOk={handleForceUpdate}
                onCancel={handleCancelForce}
                okText="强制更新"
                okButtonProps={{ danger: true }}
                cancelText="放弃修改"
                confirmLoading={loading}
                width={520}
                zIndex={1010}
            >
                <Alert
                    type="warning"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    message="检测到批次号已存在"
                    description={
                        <div style={{ marginTop: 8 }}>
                            <Text>
                                批次号{" "}
                                <Text strong code>
                                    {pendingBatchNumber}
                                </Text>{" "}
                                已被以下记录使用：
                            </Text>
                            {duplicateInfo?.existing_records && (
                                <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                    {duplicateInfo.existing_records.slice(0, 5).map((record, index) => (
                                        <li key={index}>
                                            <Text type="secondary">{record.original_filename || record.id}</Text>
                                        </li>
                                    ))}
                                    {duplicateInfo.existing_records.length > 5 && (
                                        <li>
                                            <Text type="secondary">...还有 {duplicateInfo.existing_records.length - 5} 条记录</Text>
                                        </li>
                                    )}
                                </ul>
                            )}
                            <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
                                点击"强制更新"将使选中的 {selectedIds.length} 条记录与现有记录共享同一批次号。
                            </Text>
                        </div>
                    }
                    style={{ marginBottom: 0 }}
                />
            </Modal>
        </>
    );
};

export default BatchUpdateBatchNumberModal;
